// src/App.tsx
import Layout from './components/Layout/Layout';
import { KanbanAPI, NotificationItem, primeCsrf } from './pages/kanban';

import { BrowserRouter, HashRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { BackgroundProvider } from './contexts/BackgroundContext';
import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { createAdaptivePoller } from './lib/polling';
import { apiJson } from './lib/apiClient';
import { csrfFetch } from './utils/csrfFetch';
import { BACKEND } from './config';
import { startPushChannel, getPushStatus, nextPushLive, PushSignal } from './lib/pushChannel';
import { startAppVersionWatch } from './lib/appVersionWatch';
import { ErrorBoundary } from './components/ErrorBoundary';
import LoadingSpinner from './components/Spinner/LoadingSpinner';

const SheetsPage = lazy(() => import('./pages/SheetsPage'));
const ChairmanHomePage = lazy(() => import('./pages/ChairmanHomePage'));
const ChsiPage = lazy(() => import('./pages/ChsiPage'));
const SchetaAutogenPage = lazy(() => import('./pages/SchetaAutogenPage'));
const ChsiSummaryPage = lazy(() => import('./pages/ChsiSummaryPage'));
const StatementsPage = lazy(() => import('./pages/StatementsPage'));
const AgentsDataPage = lazy(() => import('./pages/AgentsDataPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const SupportPage = lazy(() => import('./pages/SupportPage'));
const TasksPage = lazy(() => import('./pages/TasksPage'));
const DocumentsPage = lazy(() => import('./pages/DocumentsPage'));
const DossierPage = lazy(() => import('./pages/DossierPage'));
const SummaryPage = lazy(() => import('./pages/SummaryPage'));
const TimesheetPage = lazy(() => import('./pages/TimesheetPage'));
const HistoryPage = lazy(() => import('./pages/HistoryPage'));
const ActivityPage = lazy(() => import('./pages/ActivityPage'));
const LitigationSummaryPage = lazy(() => import('./pages/LitigationSummaryPage'));
const LegalTemplatesPage = lazy(() => import('./pages/LegalTemplatesPage'));
const LitigationStatusPage = lazy(() => import('./pages/LitigationStatusPage'));
const ClosedCasesPage = lazy(() => import('./pages/ClosedCasesPage'));
const CourtAdminPage = lazy(() => import('./pages/CourtAdminPage'));
const ErrorWatchPage = lazy(() => import('./pages/ErrorWatchPage'));
const UsersAdminPage = lazy(() => import('./pages/UsersAdminPage'));
const SecurityAuditPage = lazy(() => import('./pages/SecurityAuditPage'));
const MediationPage = lazy(() => import('./pages/MediationPage'));
const LitigationPage = lazy(() => import('./pages/LitigationPage'));
const RepresentativeEmployeesPage = lazy(() => import('./pages/RepresentativeEmployeesPage'));
const RepresentativeCasePage = lazy(() => import('./pages/RepresentativeCasePage'));
const RepresentativeHistoryPage = lazy(() => import('./pages/RepresentativeHistoryPage'));
const ChairmanCourtPage = lazy(() => import('./pages/ChairmanCourtPage'));
const ManagerSummaryPage = lazy(() => import('./pages/ManagerSummaryPage'));
const AiChatPage = lazy(() => import('./pages/AiChatPage'));
const ChatsPage = lazy(() => import('./pages/ChatsPage'));
const FileManagerPage = lazy(() => import('./pages/FileManagerPage'));
const AdminManagedFilesPage = lazy(() => import('./pages/AdminManagedFilesPage'));

type LocalDeadlineReminder = {
  id: number;
  userId: number;
  taskId: number;
  taskTitle: string;
  fireAt: string;
  minutes: number;
};

const DEADLINE_REMINDER_KEY = (scope: string) => `kanban:deadline-reminders:${scope}`;

type DataSyncState = {
  scope: string;
  changed: boolean;
  latest_event_at: string | null;
  version?: string;
  entity?: string;
  excel_file_id?: number;
  row_id?: number;
  cell_id?: string;
  field?: string;
  value?: unknown;
  value_type?: 'number' | 'string' | 'boolean' | 'null';
  action?: 'upsert' | 'deleted' | 'patched' | 'cell_patched' | 'cell_deleted';
  event_id?: string;
  source_action?: string;
  occurred_at?: string;
};

const NotificationModal: React.FC<{
  n: NotificationItem;
  onOk: () => void;
  onNotOk: () => void;
  onClose: () => void;
}> = ({ n, onOk, onNotOk, onClose }) => {
  const node = useMemo(() => document.body, []);

  const modal = (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 999999, // выше всего
        pointerEvents: 'none',
      }}
    >
      {/* overlay */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(15, 23, 42, 0.42)',
          pointerEvents: 'none',
        }}
      />

      {/* box: TOP-CENTER */}
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: 'absolute',
          top: 20,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 'min(560px, calc(100vw - 24px))',
          background: '#fff',
          borderRadius: 14,
          border: '1px solid #d8dee7',
          boxShadow: '0 18px 40px rgba(15, 23, 42, 0.18)',
          padding: 16,
          pointerEvents: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8, color: '#0f172a' }}>
              {n.title}
            </div>
            <div
              style={{ whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.45, color: '#334155' }}
            >
              {n.message}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              border: '1px solid #d8dee7',
              borderRadius: 10,
              background: '#ffffff',
              cursor: 'pointer',
              fontSize: 18,
              lineHeight: 1,
              width: 34,
              height: 34,
            }}
            aria-label="Close"
            title="Закрыть"
          >
            ✕
          </button>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 12 }}>
          <button
            onClick={onNotOk}
            style={{
              padding: '8px 12px',
              cursor: 'pointer',
              borderRadius: 10,
              border: '1px solid #d8dee7',
              background: '#fff',
              color: '#334155',
              fontWeight: 600,
            }}
          >
            ✕
          </button>

          <button
            onClick={onOk}
            style={{
              padding: '8px 12px',
              cursor: 'pointer',
              borderRadius: 10,
              border: '1px solid #1f4fa3',
              background: '#1f4fa3',
              color: '#fff',
              fontWeight: 700,
            }}
          >
            ОК
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, node);
};

export const NotificationsListener: React.FC = () => {
  const { isAuthenticated, userId, role } = useAuth();
  const timerRef = useRef<number | null>(null);
  const dismissedRef = useRef<Set<number>>(new Set());
  const [pushLive, setPushLive] = useState<boolean>(() => getPushStatus() === 'live');

  const storageKey = useMemo(() => `kanban:dismissed:${userId ?? 'guest'}`, [userId]);
  const reminderStorageKey = useMemo(
    () => DEADLINE_REMINDER_KEY(String(userId ?? 'guest')),
    [userId]
  );

  const pullDueReminderNotifications = useCallback((): NotificationItem[] => {
    try {
      const raw = localStorage.getItem(reminderStorageKey);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];

      const nowTs = Date.now();
      const due: NotificationItem[] = [];
      const keep: LocalDeadlineReminder[] = [];

      parsed.forEach((item) => {
        const reminder = item as LocalDeadlineReminder;
        if (!reminder || reminder.userId !== userId) return;
        const fireTs = new Date(reminder.fireAt).getTime();
        if (!Number.isFinite(fireTs)) return;

        if (fireTs <= nowTs) {
          due.push({
            id: reminder.id,
            type: 'deadline_reminder',
            title: 'Напоминание по задаче',
            message: `Пора заняться задачей: ${reminder.taskTitle}`,
            payload: { task_id: reminder.taskId, minutes: reminder.minutes },
            created_at: new Date().toISOString(),
            is_read: false,
          });
        } else {
          keep.push(reminder);
        }
      });

      localStorage.setItem(reminderStorageKey, JSON.stringify(keep));
      return due;
    } catch {
      return [];
    }
  }, [reminderStorageKey, userId]);

  const loadDismissed = React.useCallback(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return new Set<number>();
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return new Set<number>();
      return new Set<number>(parsed.filter((id) => Number.isInteger(id)));
    } catch {
      return new Set<number>();
    }
  }, [storageKey]);

  const saveDismissed = React.useCallback(
    (next: Set<number>) => {
      try {
        localStorage.setItem(storageKey, JSON.stringify(Array.from(next)));
      } catch {
        // ignore storage errors
      }
    },
    [storageKey]
  );

  const [queue, setQueue] = useState<NotificationItem[]>([]);
  const current = queue[0] ?? null;

  const ack = async (id: number, response: 'ok' | 'not_ok') => {
    if (id < 0) return;
    try {
      await KanbanAPI.ackNotification(id, response);
    } catch (e) {
      console.error('Не удалось подтвердить уведомление', e);
    }
  };

  const pop = () => setQueue((q) => q.slice(1));

  const respondAndPop = (id: number, response: 'ok' | 'not_ok') => {
    dismissedRef.current.add(id);
    saveDismissed(dismissedRef.current);
    pop();
    void ack(id, response);
  };

  const poll = useCallback(async () => {
    if (!isAuthenticated) return;
    const incoming = await KanbanAPI.listNotifications(1);
    const localReminders = pullDueReminderNotifications();
    const mergedIncoming = [...incoming, ...localReminders];

    setQueue((prev) => {
      const existingIds = new Set(prev.map((n) => n.id));
      const next = [...prev];
      for (const n of mergedIncoming) {
        if (!existingIds.has(n.id) && !dismissedRef.current.has(n.id)) next.push(n);
      }
      return next;
    });
  }, [isAuthenticated, pullDueReminderNotifications]);

  useEffect(() => {
    dismissedRef.current = loadDismissed();
  }, [loadDismissed]);

  // Пуш-канал: живой статус — реже поллим (шлюз сам разбудит немедленным сигналом ниже).
  // pushLive стартует и остаётся false, пока PUSH_ENABLED=false на бэке — интервал ниже
  // не меняется, поведение до включения шлюза не отличается от прежнего. nextPushLive даёт
  // grace через транзитный 'connecting' штатной ротации шлюза — см. pushChannel.ts.
  useEffect(() => {
    const onPushStatus = () => setPushLive((prev) => nextPushLive(prev, getPushStatus()));
    window.addEventListener('crm:push-status', onPushStatus);
    return () => window.removeEventListener('crm:push-status', onPushStatus);
  }, []);

  useEffect(() => {
    if (!isAuthenticated || role === 'error_analyst') return;
    const onPushSignal = (event: Event) => {
      const detail = (event as CustomEvent<PushSignal>).detail;
      if (detail?.scope === 'notifications') void poll();
    };
    window.addEventListener('crm:push-signal', onPushSignal);
    return () => window.removeEventListener('crm:push-signal', onPushSignal);
  }, [isAuthenticated, role, poll]);

  useEffect(() => {
    // error_analyst изолирован: не поллит уведомления (API-лок вернёт 403 — не шуметь).
    if (!isAuthenticated || role === 'error_analyst') {
      setQueue([]);
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    const stopPolling = createAdaptivePoller(() => poll(), {
      intervalMs: pushLive ? 60000 : 15000,
      hiddenIntervalMs: 30000,
      maxIntervalMs: 60000,
    });
    return () => {
      stopPolling();
      timerRef.current = null;
    };
  }, [isAuthenticated, userId, poll, role, pushLive]);

  if (!current) return null;

  return (
    <NotificationModal
      n={current}
      onOk={() => {
        respondAndPop(current.id, 'ok');
      }}
      onNotOk={() => {
        respondAndPop(current.id, 'not_ok');
      }}
      onClose={() => {
        respondAndPop(current.id, 'not_ok');
      }}
    />
  );
};

const pathToScope = (pathname: string, role?: string | null): string => {
  if (pathname.startsWith('/tasks')) return 'kanban';
  if (pathname.startsWith('/documents') || pathname.startsWith('/statements-list'))
    return 'documents';
  if (pathname.startsWith('/chsi')) return 'chsi';
  if (pathname.startsWith('/summary')) return 'summary';
  if (pathname.startsWith('/chairman/case')) return 'representative';
  if (pathname.startsWith('/chairman')) return 'sheets';

  // Для админа/менеджера/председателя на основном табличном контуре
  // берём global, иначе можно пропустить изменения из смежных модулей
  // (например, загрузки документов, влияющие на агрегаты).
  if (pathname.startsWith('/sheets') || pathname.startsWith('/dossier')) {
    if (role === 'admin' || role === 'manager' || role === 'chairman') return 'global';
    return 'sheets';
  }

  return 'global';
};

const shouldPreferSoftRefresh = (pathname: string): boolean => {
  return (
    pathname.startsWith('/sheets') ||
    pathname.startsWith('/scheta-autogen') ||
    pathname.startsWith('/documents') ||
    pathname.startsWith('/summary') ||
    pathname.startsWith('/chairman') ||
    pathname.startsWith('/chsi')
  );
};

export const GlobalDataSyncListener: React.FC<{
  onChanged: (options?: { hard?: boolean; scope?: string; event?: DataSyncState }) => void;
}> = ({ onChanged }) => {
  const { isAuthenticated, userId, role } = useAuth();
  const { pathname } = useLocation();
  const timerRef = useRef<number | null>(null);
  const lastSoftRefreshAtRef = useRef<number>(0);
  const reconnectTimerRef = useRef<number | null>(null);
  const sourceRef = useRef<EventSource | null>(null);
  const dispatchTimerRef = useRef<number | null>(null);
  const pendingEventRef = useRef<DataSyncState | null>(null);
  const reconnectAttemptRef = useRef<number>(0);
  const sseErrorCountRef = useRef<number>(0);
  const focusRetryHandlerRef = useRef<(() => void) | null>(null);
  const [sseFailed, setSseFailed] = useState(false);
  // Пуш-канал: пока PUSH_ENABLED=false на бэке (или шлюз недоступен) статус никогда не
  // становится 'live' — pushLive остаётся false навсегда, легаси-эффект ниже ни разу не
  // получает новую зависимость на деле, поведение прода не меняется до включения шлюза.
  const [pushLive, setPushLive] = useState<boolean>(() => getPushStatus() === 'live');

  const scope = useMemo(() => pathToScope(pathname, role), [pathname, role]);
  const storageKey = useMemo(
    () => `crm:data-sync:last:${userId ?? 'guest'}:${scope}`,
    [userId, scope]
  );
  const softSyncDispatchDelayMs = 300;

  const dispatchSyncChanged = useCallback(
    (event: DataSyncState) => {
      lastSoftRefreshAtRef.current = Date.now();
      const prefersSoftRefresh = shouldPreferSoftRefresh(pathname);
      onChanged({ hard: !prefersSoftRefresh, scope, event });
    },
    [onChanged, pathname, scope]
  );

  const scheduleSyncChanged = useCallback(
    (event: DataSyncState) => {
      pendingEventRef.current = event;
      if (dispatchTimerRef.current != null) return;

      dispatchTimerRef.current = window.setTimeout(() => {
        dispatchTimerRef.current = null;
        const pending = pendingEventRef.current;
        pendingEventRef.current = null;
        if (pending) {
          dispatchSyncChanged(pending);
        }
      }, softSyncDispatchDelayMs);
    },
    [dispatchSyncChanged]
  );

  const pull = useCallback(async (): Promise<boolean> => {
    if (!isAuthenticated || document.hidden) return false;

    const sinceVersion = sessionStorage.getItem(storageKey) || '';
    const query = sinceVersion
      ? `?scope=${encodeURIComponent(scope)}&since_version=${encodeURIComponent(sinceVersion)}`
      : `?scope=${encodeURIComponent(scope)}`;

    const data = await apiJson<DataSyncState>(`${BACKEND}/api/data-sync/state/${query}`);
    if (data.version) {
      sessionStorage.setItem(storageKey, data.version);
    } else if (data.latest_event_at) {
      // fallback для обратной совместимости
      sessionStorage.setItem(storageKey, data.latest_event_at);
    }

    if (data.changed) {
      scheduleSyncChanged(data);
      return true;
    }

    return false;
  }, [isAuthenticated, scope, storageKey, scheduleSyncChanged]);

  // Слушаем 'crm:push-status' независимо от isAuthenticated/role — startPushChannel сам
  // не запускается для error_analyst/анонима, поэтому статус там и так останется 'disabled'.
  // nextPushLive даёт grace через транзитный 'connecting' штатной ротации шлюза — см. pushChannel.ts.
  useEffect(() => {
    const onPushStatus = () => setPushLive((prev) => nextPushLive(prev, getPushStatus()));
    window.addEventListener('crm:push-status', onPushStatus);
    return () => window.removeEventListener('crm:push-status', onPushStatus);
  }, []);

  // Единственная точка запуска пуш-канала на вкладку (§3 плана).
  useEffect(() => {
    if (!isAuthenticated || role === 'error_analyst') return;
    const stop = startPushChannel();
    return () => stop();
  }, [isAuthenticated, role]);

  // Сигнал шлюза -> существующий pull() (дебаунс уже внутри pull-пайплайна через
  // scheduleSyncChanged); 'crm:push-open' — сверка версии сразу после (ре)коннекта/рестарта
  // шлюза, закрывает at-most-once потерю Redis pub/sub (§0 плана).
  useEffect(() => {
    if (!isAuthenticated || role === 'error_analyst') return;
    const onPushSignal = (event: Event) => {
      const detail = (event as CustomEvent<PushSignal>).detail;
      if (!detail) return;
      if (detail.scope === 'global' || detail.scope === scope) {
        void pull();
      }
    };
    const onPushOpen = () => {
      void pull();
    };
    window.addEventListener('crm:push-signal', onPushSignal);
    window.addEventListener('crm:push-open', onPushOpen);
    return () => {
      window.removeEventListener('crm:push-signal', onPushSignal);
      window.removeEventListener('crm:push-open', onPushOpen);
    };
  }, [isAuthenticated, role, scope, pull]);

  useEffect(() => {
    // error_analyst изолирован: не открывает data-sync SSE (API-лок вернёт 403 → без reconnect-шторма).
    if (!isAuthenticated || role === 'error_analyst') {
      if (reconnectTimerRef.current) {
        window.clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (dispatchTimerRef.current != null) {
        window.clearTimeout(dispatchTimerRef.current);
        dispatchTimerRef.current = null;
      }
      sourceRef.current?.close();
      sourceRef.current = null;
      pendingEventRef.current = null;
      reconnectAttemptRef.current = 0;
      sseErrorCountRef.current = 0;
      setSseFailed(false);
      return;
    }

    // Пуш-канал live — шлюз уже держит соединение, легаси Django-SSE не открываем
    // (§3 плана: "статус live -> старый EventSource НЕ открывается"). pushLive стартует
    // false и остаётся false, пока PUSH_ENABLED=false — эта ветка тогда не задействуется.
    if (pushLive) {
      if (reconnectTimerRef.current) {
        window.clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (dispatchTimerRef.current != null) {
        window.clearTimeout(dispatchTimerRef.current);
        dispatchTimerRef.current = null;
      }
      sourceRef.current?.close();
      sourceRef.current = null;
      pendingEventRef.current = null;
      reconnectAttemptRef.current = 0;
      sseErrorCountRef.current = 0;
      setSseFailed(false);
      return;
    }

    const reconnectDelaysMs = [1000, 2000, 5000, 10000, 30000];
    const maxSseErrorsBeforePolling = 5;

    const connect = () => {
      const sinceVersion = sessionStorage.getItem(storageKey) || '';
      const query = sinceVersion
        ? `?scope=${encodeURIComponent(scope)}&since_version=${encodeURIComponent(sinceVersion)}`
        : `?scope=${encodeURIComponent(scope)}`;

      const source = new EventSource(`${BACKEND}/api/data-sync/stream/${query}`, {
        withCredentials: true,
      });
      sourceRef.current = source;

      const onChangedEvent = (event: Event) => {
        const msg = event as MessageEvent<string>;
        let parsed: DataSyncState = { scope, changed: true, latest_event_at: null };
        try {
          parsed = JSON.parse(msg.data || '{}') as DataSyncState;
          if (parsed.version) {
            sessionStorage.setItem(storageKey, parsed.version);
          } else if (parsed.latest_event_at) {
            sessionStorage.setItem(storageKey, parsed.latest_event_at);
          }
        } catch (error) {
          console.debug('[soft-sync] fallback_full_refresh', {
            reason: 'invalid_payload',
            scope,
            error,
          });
        }

        scheduleSyncChanged(parsed);
      };

      source.addEventListener('data_sync_changed', onChangedEvent);
      source.onopen = () => {
        reconnectAttemptRef.current = 0;
        sseErrorCountRef.current = 0;
        setSseFailed(false);
      };
      source.onerror = () => {
        source.removeEventListener('data_sync_changed', onChangedEvent);
        source.close();

        sseErrorCountRef.current += 1;
        const reconnectDelay =
          reconnectDelaysMs[Math.min(reconnectAttemptRef.current, reconnectDelaysMs.length - 1)];
        reconnectAttemptRef.current += 1;

        if (sseErrorCountRef.current >= maxSseErrorsBeforePolling) {
          console.debug('[soft-sync] sse_error_fallback_polling', {
            scope,
            errors: sseErrorCountRef.current,
            reconnect_delay_ms: reconnectDelay,
          });
          setSseFailed(true);
          // СТОП бесконечному SSE-реконнекту (мёртвая сессия молотила 401 всю ночь —
          // night_watch 2026-07-03). Дальше работает since-поллинг; SSE вернём по
          // фокусу окна, если check-session подтвердит живую сессию (401 при мёртвой
          // сам уронит isAuthenticated через session-expiry).
          if (!focusRetryHandlerRef.current) {
            const onFocus = () => {
              void csrfFetch(`${BACKEND}/api/check-session/`)
                .then((r) => (r.ok ? r.json() : null))
                .then((d) => {
                  if (d?.authenticated) {
                    window.removeEventListener('focus', onFocus);
                    focusRetryHandlerRef.current = null;
                    sseErrorCountRef.current = 0;
                    reconnectAttemptRef.current = 0;
                    setSseFailed(false);
                    connect();
                  }
                })
                .catch(() => {
                  /* сеть — попробуем на следующем фокусе */
                });
            };
            focusRetryHandlerRef.current = onFocus;
            window.addEventListener('focus', onFocus);
          }
          return;
        }

        if (reconnectTimerRef.current) window.clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = window.setTimeout(() => {
          reconnectTimerRef.current = null;
          connect();
        }, reconnectDelay);
      };
    };

    connect();

    return () => {
      if (reconnectTimerRef.current) {
        window.clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (dispatchTimerRef.current != null) {
        window.clearTimeout(dispatchTimerRef.current);
        dispatchTimerRef.current = null;
      }
      sourceRef.current?.close();
      sourceRef.current = null;
      pendingEventRef.current = null;
      if (focusRetryHandlerRef.current) {
        window.removeEventListener('focus', focusRetryHandlerRef.current);
        focusRetryHandlerRef.current = null;
      }
    };
  }, [isAuthenticated, scope, storageKey, scheduleSyncChanged, role, pushLive]);

  useEffect(() => {
    if (!isAuthenticated) {
      if (timerRef.current) window.clearInterval(timerRef.current);
      timerRef.current = null;
      return;
    }
    if (!sseFailed) {
      return;
    }

    const stopPolling = createAdaptivePoller(
      async () => {
        const changed = await pull();
        if (changed) return;
      },
      { intervalMs: 5000, hiddenIntervalMs: 15000, maxIntervalMs: 30000 }
    );

    return () => {
      stopPolling();
      timerRef.current = null;
    };
  }, [isAuthenticated, sseFailed, pull, onChanged]);

  return null;
};

const DefaultPage: React.FC = () => {
  const { role, isLawyer, isMediator } = useAuth();
  const roleCode = String(role || '');
  if (roleCode === 'error_analyst') return <Navigate to="/error-watch" replace />;
  if (roleCode === 'court_admin') return <Navigate to="/court-admin" replace />;
  if (roleCode === 'chairman') return <Navigate to="/chairman" replace />;
  if (isLawyer) return <Navigate to="/litigation" replace />;
  if (!isLawyer && isMediator) return <Navigate to="/mediation" replace />;
  if (roleCode === 'notary' || roleCode === 'secondary') return <Navigate to="/summary" replace />;
  if (roleCode === 'representative_employee') return <Navigate to="/chairman" replace />;
  return <SheetsPage />;
};

// Контроль подачи в суд — только роль court_admin (и admin). Остальных отправляем «домой».
const CourtAdminRoute: React.FC = () => {
  const { role } = useAuth();
  const roleCode = String(role || '');
  if (roleCode !== 'court_admin' && roleCode !== 'admin') return <Navigate to="/" replace />;
  return <CourtAdminPage />;
};

// Ошибки сервера (мини-ловец) — роли admin и error_analyst. Остальных отправляем «домой».
const ErrorWatchRoute: React.FC = () => {
  const { role } = useAuth();
  if (!['admin', 'error_analyst'].includes(String(role || ''))) return <Navigate to="/" replace />;
  return <ErrorWatchPage />;
};

// Управление учётками CRM — ТОЛЬКО admin (error_analyst сюда не пускаем). Остальных — «домой».
const UsersAdminRoute: React.FC = () => {
  const { role } = useAuth();
  if (String(role || '') !== 'admin') return <Navigate to="/" replace />;
  return <UsersAdminPage />;
};

// Журнал безопасности — ТОЛЬКО admin. Остальных — «домой».
const SecurityAuditRoute: React.FC = () => {
  const { role } = useAuth();
  if (String(role || '') !== 'admin') return <Navigate to="/" replace />;
  return <SecurityAuditPage />;
};

const SheetsRoute: React.FC = () => {
  const { role, isLawyer, isMediator } = useAuth();
  const roleCode = String(role || '');
  if (roleCode === 'secondary' || isLawyer || isMediator) return <Navigate to="/summary" replace />;
  return <SheetsPage />;
};

const SummaryRoute: React.FC = () => {
  const { isLawyer, isMediator } = useAuth();
  if (isLawyer) return <Navigate to="/litigation" replace />;
  if (!isLawyer && isMediator) return <Navigate to="/mediation" replace />;
  return <SummaryPage />;
};

const DossierRoute: React.FC = () => {
  const { isLawyer, isMediator } = useAuth();
  if (isLawyer) return <Navigate to="/litigation" replace />;
  if (!isLawyer && isMediator) return <Navigate to="/mediation" replace />;
  return <DossierPage />;
};

const TimesheetRoute: React.FC = () => {
  const { username } = useAuth();
  const allowedTimesheetUsers = new Set(['serik', 'suresan', 'bauka']);
  if (!username || !allowedTimesheetUsers.has(username.toLowerCase()))
    return <Navigate to="/" replace />;
  return <TimesheetPage />;
};

const DocumentsRoute: React.FC = () => {
  const { role, isLawyer, isMediator } = useAuth();
  const roleCode = String(role || '');
  if (roleCode === 'notary' || roleCode === 'secondary' || isLawyer || isMediator)
    return <Navigate to="/summary" replace />;
  return <DocumentsPage />;
};

const ChsiRoute: React.FC = () => {
  const { role } = useAuth();
  const roleCode = String(role || '');
  if (roleCode === 'notary') return <Navigate to="/summary" replace />;
  return <ChsiPage />;
};

// «Автоген счетов» — только admin/manager/employee (как основной табличный контур).
const SchetaAutogenRoute: React.FC = () => {
  const { role, isLawyer, isMediator } = useAuth();
  const roleCode = String(role || '');
  if (!['admin', 'manager', 'employee'].includes(roleCode) || isLawyer || isMediator) {
    return <Navigate to="/summary" replace />;
  }
  return <SchetaAutogenPage />;
};

const HistoryRoute: React.FC = () => {
  const { role } = useAuth();
  const roleCode = String(role || '');
  if (roleCode !== 'admin' && roleCode !== 'manager') return <Navigate to="/" replace />;
  return <HistoryPage />;
};

const ActivityRoute: React.FC = () => {
  const { role } = useAuth();
  const roleCode = String(role || '');
  if (roleCode !== 'admin' && roleCode !== 'manager') return <Navigate to="/" replace />;
  return <ActivityPage />;
};

// История изменений для представителей (chairman + сотрудник) — без автора правки.
const RepresentativeHistoryRoute: React.FC = () => {
  const { role } = useAuth();
  const roleCode = String(role || '');
  if (roleCode !== 'chairman' && roleCode !== 'representative_employee')
    return <Navigate to="/" replace />;
  return <RepresentativeHistoryPage />;
};

const ManagerSummaryRoute: React.FC = () => {
  const { role } = useAuth();
  const roleCode = String(role || '');
  // employee — урезанный режим «Сводка» (страница сама режет UI по роли).
  if (roleCode !== 'admin' && roleCode !== 'manager' && roleCode !== 'employee') {
    return <Navigate to="/" replace />;
  }
  return <ManagerSummaryPage />;
};

const AiChatRoute: React.FC = () => {
  const { role, aiChatExtraAccess } = useAuth();
  const roleCode = String(role || '');
  // Чат ИИ — менеджерские роли + точечный доступ по полю CustomUser.ai_chat_extra_access
  // (2026-08-06, админка), приходит в сессии из /api/check-session/ (бэк дублирует гейт —
  // views_ai_assistant.py _ai_chat_access_required).
  if (roleCode !== 'admin' && roleCode !== 'manager' && !aiChatExtraAccess)
    return <Navigate to="/" replace />;
  return <AiChatPage />;
};

const ChatsRoute: React.FC = () => {
  const { role } = useAuth();
  const roleCode = String(role || '');
  // Чаты WhatsApp: менеджеру — все, сотруднику — только назначенные ему.
  if (roleCode !== 'admin' && roleCode !== 'manager' && roleCode !== 'employee') {
    return <Navigate to="/" replace />;
  }
  return <ChatsPage />;
};

const RouterComponent = (window as any).desktopApp ? HashRouter : BrowserRouter;

const App: React.FC = () => {
  const handleSyncChange = useCallback(
    (options?: { hard?: boolean; scope?: string; event?: DataSyncState }) => {
      window.dispatchEvent(
        new CustomEvent('crm:data-sync-soft', {
          detail: {
            scope: options?.scope || options?.event?.scope || 'global',
            entity: options?.event?.entity || 'registry',
            excel_file_id: options?.event?.excel_file_id,
            action: options?.event?.action,
            row_id: options?.event?.row_id,
            cell_id: options?.event?.cell_id,
            field: options?.event?.field,
            value: options?.event?.value,
            value_type: options?.event?.value_type,
            version: options?.event?.version,
            event_id: options?.event?.event_id,
            source_action: options?.event?.source_action,
            occurred_at: options?.event?.occurred_at || options?.event?.latest_event_at,
            changed: options?.event?.changed,
            latest_event_at: options?.event?.latest_event_at,
            hard_requested: Boolean(options?.hard),
          },
        })
      );
    },
    []
  );

  useEffect(() => {
    primeCsrf().catch(() => {});
  }, []);

  // Автообновление клиента: вкладка перезагружается сама, когда бэкенд начал отдавать другую
  // версию сборки. Без этого долгоживущая вкладка неделями работает на коде, несовместимом с
  // сервером, и отказ выглядит как ошибка функции — 18.08.2026 так пропали отмены у
  // представителя. Перенос в Nova: да (правка не-доменная). См. lib/appVersionWatch.ts и
  // docs/tasks/2026-08-18-client-version-autoreload.md.
  useEffect(() => startAppVersionWatch(), []);

  return (
    <BackgroundProvider>
      <RouterComponent>
        <NotificationsListener />
        <GlobalDataSyncListener onChanged={handleSyncChange} />

        <Suspense fallback={<LoadingSpinner />}>
          <ErrorBoundary>
            <Routes>
              <Route path="/login" element={<LoginPage />} />

              <Route path="/" element={<Layout />}>
                <Route index element={<DefaultPage />} />
                <Route path="sheets" element={<SheetsRoute />} />
                <Route path="tasks" element={<TasksPage />} />
                <Route path="documents" element={<DocumentsRoute />} />
                <Route path="dossier" element={<DossierRoute />} />
                <Route path="chairman" element={<ChairmanHomePage />} />
                <Route path="chsi" element={<ChsiRoute />} />
                <Route path="scheta-autogen" element={<SchetaAutogenRoute />} />
                <Route path="chsi-summary" element={<ChsiSummaryPage />} />
                <Route path="statements-list" element={<StatementsPage />} />
                <Route path="agents-data" element={<AgentsDataPage />} />
                <Route path="support" element={<SupportPage />} />
                <Route path="history" element={<ActivityRoute />} />
                <Route path="history-changes" element={<HistoryRoute />} />
                <Route path="manager-summary" element={<ManagerSummaryRoute />} />
                <Route path="ai-chat" element={<AiChatRoute />} />
                <Route path="chats" element={<ChatsRoute />} />
                <Route path="summary" element={<SummaryRoute />} />
                <Route path="timesheet" element={<TimesheetRoute />} />
                <Route path="chairman/employees" element={<RepresentativeEmployeesPage />} />
                <Route path="chairman/case" element={<RepresentativeCasePage />} />
                <Route path="chairman/history" element={<RepresentativeHistoryRoute />} />
                <Route path="chairman/court" element={<ChairmanCourtPage />} />
                <Route path="litigation" element={<LitigationPage />} />
                <Route path="mediation" element={<MediationPage />} />
                <Route path="litigation-status" element={<LitigationStatusPage />} />
                <Route path="litigation-closed" element={<ClosedCasesPage />} />
                <Route path="court-admin" element={<CourtAdminRoute />} />
                <Route path="error-watch" element={<ErrorWatchRoute />} />
                <Route path="users-admin" element={<UsersAdminRoute />} />
                <Route path="security-audit" element={<SecurityAuditRoute />} />
                <Route path="templates" element={<LegalTemplatesPage />} />
                <Route path="litigation-summary" element={<LitigationSummaryPage />} />
                <Route path="file-manager" element={<FileManagerPage />} />
                <Route path="admin/managed-files" element={<AdminManagedFilesPage />} />
              </Route>
            </Routes>
          </ErrorBoundary>
        </Suspense>
      </RouterComponent>
    </BackgroundProvider>
  );
};

export default App;
